/**
 * Recordings API Service
 * Handles all communication with backend and video-engine for recordings
 */

import axios, { type AxiosProgressEvent } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009';
const VIDEO_ENGINE_URL = process.env.REACT_APP_VIDEO_ENGINE_URL || 'http://localhost:8080';
const MIN_VALID_EXPORT_BYTES = 64 * 1024;

/** Envía la hora con offset de zona (ej. -05:00) para que backend y video-engine reciban el instante correcto, igual que en playback. */
/** Progreso de generación en servidor (ZIP/PDF), vía GET /recordings/export-progress/:sessionId */
export async function fetchExportGenerationProgress(
  sessionId: string,
  token: string
): Promise<{ percent: number; label: string } | null> {
  try {
    const r = await axios.get<{ success?: boolean; data?: { percent: number; label: string } }>(
      `${API_URL}/recordings/export-progress/${encodeURIComponent(sessionId)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (s: number) => s === 200,
      }
    );
    return r.data?.data ?? null;
  } catch {
    return null;
  }
}

function toLocalISOString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const offsetMin = -d.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const h = Math.floor(Math.abs(offsetMin) / 60);
  const m = Math.abs(offsetMin) % 60;
  const tz = `${sign}${pad(h)}:${pad(m)}`;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.000${tz}`;
}

export interface Recording {
  id: string;
  cameraId: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  filePath?: string;
  fileName?: string;
  fileSize?: number;
  format?: string;
  resolution?: string;
  fps?: number;
  recordingType: 'continuous' | 'event' | 'manual';
  storageProvider: 'local' | 's3';
  camera?: {
    id: string;
    name: string;
    status: string;
  };
}

export interface TimelineSegment {
  id: string;
  cameraId: string;
  startTime: Date;
  endTime: Date;
  duration: number;
  fileSize?: number;
  storageProvider: string;
  recordingType: string;
  hasGap?: boolean;
}

export interface TimelineData {
  cameraId: string;
  segments: TimelineSegment[];
  totalDuration: number;
  totalSize: number;
  gaps: { startTime: Date; endTime: Date }[];
  startTime: Date;
  endTime: Date;
}

export interface StorageStats {
  totalSize: number;
  totalSizeGB: number;
  totalDuration: number;
  totalDurationHours: number;
  recordingsCount: number;
  byCamera: Record<string, { size: number; sizeGB: number; duration: number; count: number }>;
  byType: Record<string, { size: number; sizeGB: number; duration: number; count: number }>;
}

class RecordingsApiService {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    // Obtener token del localStorage si no está en memoria (para asegurar token actualizado)
    const token = this.token || localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Evita `SyntaxError: Unexpected token '<'` al usar `response.json()` cuando el servidor (o un proxy) devuelve
   * HTML: 404 de SPA, 502/503 de Nginx, página de login, etc.
   */
  private formatFetchError(context: string, status: number, text: string): string {
    const t = text.trim();
    if (t.startsWith('<') || t.startsWith('<!')) {
      return (
        `${context} (${status}): se recibió HTML en lugar de JSON. ` +
        `Suele indicar URL de API errónea, cierre de sesión, o error de proxy. ` +
        `Revisa REACT_APP_API_URL (actual: ${API_URL}).`
      );
    }
    try {
      const j = JSON.parse(t) as {
        error?: { message?: string } | string;
        message?: string;
      };
      const e = j.error;
      const fromNested = typeof e === 'object' && e && 'message' in e ? String((e as { message?: string }).message) : null;
      const fromFlat = typeof e === 'string' ? e : j.message;
      const msg = fromNested || fromFlat;
      if (msg) return `${context} (${status}): ${msg}`;
    } catch {
      /* cuerpo no es JSON; usar recorte */
    }
    if (t.length) return `${context} (${status}): ${t.slice(0, 300)}${t.length > 300 ? '…' : ''}`;
    return `${context} falló (HTTP ${status})`;
  }

  private parseJsonResponseBody<T>(text: string, context: string): T {
    const t = text.trim();
    if (!t.length) {
      throw new Error(`${context}: respuesta vacía del API`);
    }
    if (t[0] === '<') {
      throw new Error(
        `${context}: cuerpo parece HTML, no JSON. ` +
          `Comprueba REACT_APP_API_URL (actual: ${API_URL}) y que el backend responda JSON en ese origen.`
      );
    }
    try {
      return JSON.parse(t) as T;
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      throw new Error(`${context}: respuesta no es JSON válido (${m})`);
    }
  }

  /**
   * Get recordings with filters
   */
  async getRecordings(params: {
    cameraIds?: string[];
    startTime?: Date;
    endTime?: Date;
    storageProvider?: string;
    recordingType?: string;
  }): Promise<Recording[]> {
    const searchParams = new URLSearchParams();
    
    if (params.cameraIds?.length) {
      searchParams.set('cameraIds', params.cameraIds.join(','));
    }
    if (params.startTime) {
      searchParams.set('startTime', params.startTime.toISOString());
    }
    if (params.endTime) {
      searchParams.set('endTime', params.endTime.toISOString());
    }
    if (params.storageProvider) {
      searchParams.set('storageProvider', params.storageProvider);
    }
    if (params.recordingType) {
      searchParams.set('recordingType', params.recordingType);
    }

    const response = await fetch(`${API_URL}/recordings?${searchParams}`, {
      headers: this.getHeaders(),
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(this.formatFetchError('Listado de grabaciones', response.status, text));
    }

    return this.parseJsonResponseBody<Recording[]>(text, 'getRecordings');
  }

  /**
   * Get timeline data for multiple cameras
   */
  async getTimeline(
    cameraIds: string[],
    startTime: Date,
    endTime: Date,
  ): Promise<TimelineData[]> {
    const searchParams = new URLSearchParams({
      cameraIds: cameraIds.join(','),
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
    });

    const response = await fetch(`${API_URL}/recordings/timeline?${searchParams}`, {
      headers: this.getHeaders(),
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(this.formatFetchError('Línea de tiempo (timeline)', response.status, text));
    }

    const data = this.parseJsonResponseBody<unknown[]>(text, 'getTimeline');
    
    // Convert string dates to Date objects
    return data.map((timeline: any) => ({
      ...timeline,
      startTime: new Date(timeline.startTime),
      endTime: new Date(timeline.endTime),
      segments: timeline.segments.map((seg: any) => ({
        ...seg,
        startTime: new Date(seg.startTime),
        endTime: new Date(seg.endTime),
      })),
      gaps: timeline.gaps.map((gap: any) => ({
        startTime: new Date(gap.startTime),
        endTime: new Date(gap.endTime),
      })),
    }));
  }

  /**
   * Get a single recording by ID
   */
  async getRecording(id: string): Promise<Recording> {
    const response = await fetch(`${API_URL}/recordings/${id}`, {
      headers: this.getHeaders(),
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(this.formatFetchError('Detalle de grabación', response.status, text));
    }

    return this.parseJsonResponseBody<Recording>(text, 'getRecording');
  }

  /**
   * Get recording at specific timestamp
   */
  async getRecordingAtTime(cameraId: string, timestamp: Date): Promise<Recording> {
    const searchParams = new URLSearchParams({
      timestamp: timestamp.toISOString(),
    });

    const response = await fetch(
      `${API_URL}/recordings/playback/${cameraId}?${searchParams}`,
      { headers: this.getHeaders() },
    );

    const text = await response.text();
    if (!response.ok) {
      throw new Error(this.formatFetchError('Playback / grabación en instante', response.status, text));
    }

    return this.parseJsonResponseBody<Recording>(text, 'getRecordingAtTime');
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(cameraId?: string): Promise<StorageStats> {
    const params = cameraId ? `?cameraId=${cameraId}` : '';
    const response = await fetch(`${API_URL}/recordings/stats${params}`, {
      headers: this.getHeaders(),
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(this.formatFetchError('Estadísticas de almacenamiento', response.status, text));
    }

    return this.parseJsonResponseBody<StorageStats>(text, 'getStorageStats');
  }

  /**
   * Get playback URL for a recording
   */
  getPlaybackUrl(recordingId: string, timestamp?: number): string {
    let url = `${VIDEO_ENGINE_URL}/api/v1/recordings/${recordingId}/play`;
    if (timestamp) {
      url += `?t=${timestamp}`;
    }
    return url;
  }

  /**
   * Get playback URL for a camera at specific time
   */
  getPlaybackUrlForCamera(cameraId: string, timestamp: Date): string {
    const recordingId = `${cameraId}-${this.formatTimestampForFilename(timestamp)}`;
    return this.getPlaybackUrl(recordingId, timestamp.getTime());
  }

  /**
   * Get HLS stream URL
   */
  getHLSStreamUrl(cameraId: string): string {
    return `${VIDEO_ENGINE_URL}/api/v1/cameras/${cameraId}/stream/stream.m3u8`;
  }

  /**
   * Get snapshot URL
   */
  getSnapshotUrl(cameraId: string): string {
    return `${VIDEO_ENGINE_URL}/api/v1/cameras/${cameraId}/snapshot`;
  }

  /**
   * Format timestamp for filename
   */
  private formatTimestampForFilename(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(date.getSeconds())}`;
  }

  /**
   * Trigger purge of old recordings
   */
  async purgeRecordings(daysOld: number): Promise<{ deletedCount: number; freedGB: number }> {
    const response = await fetch(`${API_URL}/recordings/purge`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ daysOld }),
    });

    const text = await response.text();
    if (!response.ok) {
      throw new Error(this.formatFetchError('Purgar grabaciones', response.status, text));
    }

    return this.parseJsonResponseBody<{ deletedCount: number; freedGB: number }>(text, 'purgeRecordings');
  }

  /**
   * Export recordings
   */
  async exportRecordings(cameraId: string, startTime: Date, endTime: Date): Promise<Recording[]> {
    const params = new URLSearchParams({
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
    });

    const response = await fetch(
      `${API_URL}/recordings/export/${cameraId}?${params}`,
      { headers: this.getHeaders() },
    );

    const text = await response.text();
    if (!response.ok) {
      throw new Error(this.formatFetchError('Exportar grabaciones (lista)', response.status, text));
    }

    return this.parseJsonResponseBody<Recording[]>(text, 'exportRecordings');
  }

  /**
   * Export recording as MP4 file
   * @param siteId Site ID (number)
   * @param cameraIdNum Camera ID number (devices_cameras.id)
   * @param startTime Start time
   * @param endTime End time
   * @param quality Quality option (original, 1080p, 720p, 480p)
   * @param meta packaging ts_zip = ZIP de segmentos sin FFmpeg en el motor (más rápido).
   *        onProgress: bytes recibidos mientras baja el blob (mismo stream HTTP).
   * @returns Promise that resolves with blob URL for download
   */
  async exportRecordingAsMP4(
    siteId: number,
    cameraIdNum: number,
    startTime: Date,
    endTime: Date,
    quality: string = 'original',
    meta?: {
      spoa?: string;
      packaging?: 'mp4' | 'ts_zip';
      /** Mismo requestId del prepare: el motor remuxa los s*.ts ya en disco. */
      requestId?: string;
      /** UUID v4 — habilita sondeo de generación antes de que bajen bytes del ZIP. */
      progressSessionId?: string;
      /** Bytes bajando por el mismo stream HTTP; percent solo si el servidor manda Content-Length. */
      onProgress?: (ev: { loaded: number; total?: number; percent: number | null }) => void;
      /** Cancelar desde el drawer de descargas. */
      signal?: AbortSignal;
    }
  ): Promise<{ blobUrl: string; fileName: string; blobSize: number; effectiveStart?: Date; effectiveEnd?: Date }> {
    try {
      const body: Record<string, unknown> = {
        siteId,
        cameraIdNum,
        startTime: toLocalISOString(startTime),
        endTime: toLocalISOString(endTime),
        quality,
      };
      const rid = meta?.requestId?.trim();
      if (rid) body.requestId = rid;
      const s = meta?.spoa?.trim();
      if (s) body.spoa = s;
      if (meta?.packaging === 'ts_zip' || meta?.packaging === 'mp4') {
        body.packaging = meta.packaging;
      }
      const ps = meta?.progressSessionId?.trim();
      if (ps) body.progressSessionId = ps;
      // Usar axios para aprovechar el interceptor de refresh automático del token
      const onProgressCb = meta?.onProgress
        ? (e: AxiosProgressEvent) => {
            const t = e.total;
            if (e.lengthComputable && t != null && t > 0) {
              meta.onProgress!({ loaded: e.loaded, total: t, percent: (e.loaded / t) * 100 });
            } else {
              meta.onProgress!({ loaded: e.loaded, total: t, percent: null });
            }
          }
        : undefined;
      const response = await axios.post(`${API_URL}/recordings/export`, body, {
        responseType: 'blob',
        // Sin tope: 10 min original ~150–300 MB; a 500 kbps la descarga tarda
        // ~40–80 min. Cualquier timeout fijo cortaba a mitad en internet pobre.
        // El usuario puede cancelar desde el drawer si algo cuelga de verdad.
        timeout: 0,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        validateStatus: (status: number) => status < 400,
        onDownloadProgress: onProgressCb,
        signal: meta?.signal,
      });

      // Check if response is an error (4xx) - axios won't throw with validateStatus
      if (response.status >= 400) {
        // Try to read error message from blob (it's actually JSON)
        let errorMessage = `Error al exportar la grabación (${response.status})`;
        try {
          const text = (await response.data.text()).trim();
          if (text && text[0] !== '<' && text[0] !== '<!') {
            const errorData = JSON.parse(text) as { error?: { message?: string } | string; message?: string };
            const em =
              (typeof errorData.error === 'object' && errorData.error && 'message' in errorData.error
                ? (errorData.error as { message?: string }).message
                : null) ||
              (typeof errorData.error === 'string' ? errorData.error : null) ||
              errorData.message;
            if (em) errorMessage = em;
          }
        } catch {
          // no JSON: mantener mensaje genérico
        }
        throw new Error(errorMessage);
      }

      const ctRaw = response.headers['content-type'] as string | undefined;
      const ct = ctRaw?.split(';')[0]?.trim() || 'application/octet-stream';
      const isZip = ct.includes('zip') || ct === 'application/x-zip-compressed';
      const blob = new Blob([response.data], {
        type: isZip ? 'application/zip' : ct.includes('mp4') ? 'video/mp4' : ct,
      });

      if (blob.size < MIN_VALID_EXPORT_BYTES) {
        let preview = '';
        try {
          preview = (await blob.text()).trim().slice(0, 300);
        } catch {
          /* el blob puede ser binario; el tamaño sigue siendo inválido para un export real */
        }
        throw new Error(
          `La exportación no generó un archivo de video válido (${blob.size} bytes).` +
            (preview ? ` Respuesta recibida: ${preview}` : ' Intente de nuevo con un rango donde la grabación esté disponible.')
        );
      }

      const blobUrl = URL.createObjectURL(blob);

      const defaultExt = isZip ? 'zip' : 'mp4';
      let fileName = `recording_${startTime.toISOString().slice(0, 10)}.${defaultExt}`;

      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const star = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
        if (star?.[1]) {
          try {
            fileName = decodeURIComponent(star[1]);
          } catch {
            fileName = star[1];
          }
        } else {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match?.[1]) {
            fileName = match[1].replace(/['"]/g, '');
          }
        }
      }

      const effStartRaw = response.headers['x-export-effective-start'] as string | undefined;
      const effEndRaw = response.headers['x-export-effective-end'] as string | undefined;
      const effectiveStart = effStartRaw ? new Date(effStartRaw) : undefined;
      const effectiveEnd = effEndRaw ? new Date(effEndRaw) : undefined;

      return {
        blobUrl,
        fileName,
        blobSize: blob.size,
        effectiveStart: effectiveStart && Number.isFinite(effectiveStart.getTime()) ? effectiveStart : undefined,
        effectiveEnd: effectiveEnd && Number.isFinite(effectiveEnd.getTime()) ? effectiveEnd : undefined,
      };
    } catch (error: any) {
      // Handle axios errors
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        
        // If error response is a blob (JSON error), try to read it
        if (error.response.data instanceof Blob) {
          const text = (await error.response.data.text()).trim();
          let fromApi: string | null = null;
          if (text && text[0] !== '<' && !text.startsWith('<!')) {
            try {
              const errorData = JSON.parse(text) as { error?: { message?: string } | string; message?: string };
              fromApi =
                (typeof errorData.error === 'object' && errorData.error && 'message' in errorData.error
                  ? (errorData.error as { message?: string }).message
                  : null) ||
                (typeof errorData.error === 'string' ? errorData.error : null) ||
                (typeof errorData.message === 'string' ? errorData.message : null);
            } catch {
              /* cuerpo no es JSON (no relanzar SyntaxError) */
            }
          }
          if (fromApi) throw new Error(fromApi);
          if (status === 404) {
            throw new Error('No hay grabaciones disponibles para el rango de tiempo especificado');
          }
          if (status === 400) {
            throw new Error('Solicitud inválida. Verifica el rango de tiempo seleccionado');
          }
          throw new Error(
            `Error al exportar la grabación (${status})` +
              (text[0] === '<' ? '. El servidor devolvió HTML: revisa REACT_APP_API_URL o el proxy.' : '')
          );
        }
        // If error response is already JSON
        if (error.response.data?.error) {
          const errorMsg = error.response.data.error.message || error.response.data.error || error.message;
          throw new Error(errorMsg);
        }
        
        // Fallback: use status code to provide helpful message
        if (status === 404) {
          throw new Error('No hay grabaciones disponibles para el rango de tiempo especificado');
        } else if (status === 400) {
          throw new Error('Solicitud inválida. Verifica el rango de tiempo seleccionado');
        }
      }
      // Re-throw with original message
      throw error;
    }
  }
}

// Singleton instance
export const recordingsApi = new RecordingsApiService();

export async function fetchRecordingConfig(
  token: string
): Promise<{ maxDownloadMinutes: number }> {
  const r = await axios.get<{ maxDownloadMinutes: number }>(
    `${API_URL}/recordings/config`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return r.data;
}

